import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMyEquipment } from '../../../../../hooks';
import { actions } from './actions';
import CallToAction from './CallToAction';
import AddOrEditEquipment from './AddOrEditEquipment';
import { ComponentLoader } from 'cat-ecommerce-alloy';
import Conditional from '../../../../common/Conditional';
import styles from './styles.module.scss';
import PropTypes from 'prop-types';

const DiagramsPlaceholder = ({
  hasEquipmentSaved,
  hasSerialNumber,
  hasSelectedEquipment,
  isSchematicsLoading,
  openAddDrawer,
  openEditDrawer,
  openSelectDrawer
}) => {
  const {
    ADD_EQUIPMENT,
    ADD_OR_SELECT_EQUIPMENT,
    EDIT_EQUIPMENT,
    NO_DIAGRAMS
  } = actions;

  const { t } = useTranslation();
  const [optionToRender, setOptionToRender] = useState();
  const { openSaveEquipment } = useMyEquipment();
  const showNoDiagramsAvailable = optionToRender === NO_DIAGRAMS;
  const isQRCodeTempEquipment = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment.isQRCodeTempEquipment
  );

  const renderProps = {
    [ADD_EQUIPMENT]: {
      onClick: openAddDrawer,
      title: t('DIAGRAM_PLACEHOLDER4')
    },
    [EDIT_EQUIPMENT]: {
      onClick: isQRCodeTempEquipment ? openSaveEquipment : openEditDrawer,
      title: t('ENTER_YOUR_SN')
    }
  };

  useEffect(() => {
    if (!hasEquipmentSaved) {
      setOptionToRender(ADD_EQUIPMENT);
    } else if (hasEquipmentSaved && !hasSelectedEquipment) {
      setOptionToRender(ADD_OR_SELECT_EQUIPMENT);
    } else if (hasSelectedEquipment && !hasSerialNumber) {
      setOptionToRender(EDIT_EQUIPMENT);
    } else {
      setOptionToRender(NO_DIAGRAMS);
    }
  }, [
    ADD_EQUIPMENT,
    ADD_OR_SELECT_EQUIPMENT,
    EDIT_EQUIPMENT,
    NO_DIAGRAMS,
    hasEquipmentSaved,
    hasSelectedEquipment,
    hasSerialNumber
  ]);

  const titleClassName = 'h3 u-color--ExtraDarkGray mb-0';
  const ViewDetailDiagrams = (
    <>
      <h3 className={cx(titleClassName, 'mt-3', styles['message-text'])}>
        {t('DIAGRAM_PLACEHOLDER3')}
      </h3>
      {optionToRender === ADD_OR_SELECT_EQUIPMENT ? (
        <AddOrEditEquipment
          openAddEquipment={openAddDrawer}
          openSelectEquipment={openSelectDrawer}
        />
      ) : (
        <CallToAction {...renderProps[optionToRender]} />
      )}
    </>
  );

  const NoDiagrams = (
    <div className="w-100 my-3">
      <h3
        className={cx(
          titleClassName,
          'my-3 text-break',
          styles['message-text']
        )}
      >
        {t('SIS_NO_SCHEMATIC_DIAGRAMS_TEXT')}
      </h3>
    </div>
  );

  const Loader = (
    <ComponentLoader
      size="small"
      contentClassName="p-0"
      textClassName={cx('fw-bold', styles['loader-text'])}
    >
      {t('PDP_SCHEMATIC_LOADING_DIAGRAMS_TEXT2')}
    </ComponentLoader>
  );

  return (
    <div
      className={cx(
        'd-flex position-relative w-100',
        styles['schematic-placeholder'],
        {
          [styles['bg-img-none']]: isSchematicsLoading,
          [styles['schematic-placeholder--loading']]: isSchematicsLoading
        }
      )}
      data-testid="placeholderContainer"
    >
      <div
        className={cx(
          ' d-flex flex-column justify-content-center align-items-start w-100',
          styles['message-container']
        )}
      >
        <Conditional test={!isSchematicsLoading} fallback={Loader}>
          {showNoDiagramsAvailable ? NoDiagrams : ViewDetailDiagrams}
        </Conditional>
      </div>
    </div>
  );
};

DiagramsPlaceholder.propTypes = {
  hasEquipmentSaved: PropTypes.bool.isRequired,
  hasSerialNumber: PropTypes.bool.isRequired,
  hasSelectedEquipment: PropTypes.bool.isRequired,
  isSchematicsLoading: PropTypes.bool.isRequired,
  openAddDrawer: PropTypes.func,
  openEditDrawer: PropTypes.func,
  openSelectDrawer: PropTypes.func
};

export default DiagramsPlaceholder;
